import React from 'react';
import { Spinner } from 'react-bootstrap';
import type { Stripe, StripeElements } from '@stripe/stripe-js';

import { PatientOrderingPricing } from '@customTypes/test-ordering';

import type { Consent, FormType, WizardStep } from '../types/Wizard.types';
import AddressStep from './AddressStep';
import ConsentsStep from './ConsentsStep';
import PaymentStep from './PaymentStep';
import QuestionnaireStep from './QuestionnaireStep';

interface WizardStepRendererProps {
  currentStep: WizardStep;
  values: FormType;
  showErrors: boolean;
  errors: Record<string, string>;
  updateValue: (path: string, val: unknown) => void;
  stripeError: string | null;
  stripe: Stripe | null;
  elements: StripeElements | null;
  questionTexts: Record<string, string>;
  allConsents: Consent[];
  dnaVisitConsent: Consent | null;
  hieConsent: Consent | null;
  researchConsent: Consent | null;
  transactionalSmsConsent: Consent | null;
  marketingConsent: Consent | null;
  minorConsent: Consent | null;
  loadingConsents: boolean;
  consentsFailed: boolean;
  fetchConsentsData: () => void;
  pricing: PatientOrderingPricing | undefined;
  includeFamilyHistoryAttest?: boolean;
}

/**
 * Component that renders the appropriate wizard step based on currentStep.
 * Handles all step types and includes loading/error states for consents step.
 */
export const WizardStepRenderer: React.FC<WizardStepRendererProps> = ({
  currentStep,
  values,
  showErrors,
  errors,
  updateValue,
  stripeError,
  stripe,
  elements,
  questionTexts,
  allConsents,
  dnaVisitConsent,
  hieConsent,
  researchConsent,
  transactionalSmsConsent,
  marketingConsent,
  minorConsent,
  loadingConsents,
  consentsFailed,
  fetchConsentsData,
  pricing,
  includeFamilyHistoryAttest = true,
}) => {
  switch (currentStep) {
    case 'address':
      return <AddressStep values={values} showErrors={showErrors} errors={errors} update={updateValue} />;

    case 'payment':
      return (
        <PaymentStep
          values={values}
          showErrors={showErrors}
          errors={errors}
          update={updateValue}
          stripeError={stripeError || undefined}
          stripe={stripe}
          elements={elements}
          pricing={pricing}
        />
      );

    case 'questionnaire':
      return (
        <QuestionnaireStep
          questionTexts={questionTexts}
          values={values}
          showErrors={showErrors}
          errors={errors}
          update={updateValue}
        />
      );

    case 'consents':
      if (loadingConsents) {
        return (
          <div className='wizard-loading'>
            <Spinner animation='border' role='status' aria-label='Loading consents' />
            <div className='wizard-loading__caption'>Loading consents…</div>
          </div>
        );
      }

      if (consentsFailed || (!dnaVisitConsent && allConsents.length === 0)) {
        return (
          <div className='wizard-loading'>
            <div className='po-error inline'>Unable to load consents. Please try again.</div>
            <button type='button' className='secondary mt-2' onClick={fetchConsentsData}>
              Retry
            </button>
          </div>
        );
      }

      return (
        <div className='consents-step'>
          <ConsentsStep
            dnaVisitConsent={dnaVisitConsent}
            hieConsent={hieConsent}
            researchConsent={researchConsent}
            transactionalSmsConsent={transactionalSmsConsent}
            marketingConsent={marketingConsent}
            minorConsent={minorConsent}
            allConsents={allConsents}
            values={values}
            showErrors={showErrors}
            errors={errors}
            update={updateValue}
            includeFamilyHistoryAttest={includeFamilyHistoryAttest}
          />
        </div>
      );

    default:
      return null;
  }
};
